/**
 * Usage Meter Component
 * Shows current usage vs plan limits
 */

const UsageMeter = ({ label, used, limit, unit = '', showUpgrade = false, onUpgrade, showAddOn = false, onAddOn, addOnLoading = false }) => {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isNearLimit = percentage >= 80;
  const isOverLimit = percentage >= 100;

  const getColorClass = () => {
    if (isOverLimit) return 'bg-red-600';
    if (isNearLimit) return 'bg-orange-500';
    return 'bg-primary-600';
  };

  const formatValue = (value) => {
    if (value === -1) return '∞';
    return value.toLocaleString();
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-neutral-700">{label}</span>
        <span className="text-sm text-neutral-900">
          <span className="font-semibold">{formatValue(used)}</span>
          {!isUnlimited && (
            <>
              {' / '}
              {formatValue(limit)} {unit}
            </>
          )}
          {isUnlimited && <span className="text-neutral-600 ml-1">(Unlimited)</span>}
        </span>
      </div>

      {/* Progress Bar */}
      {!isUnlimited && (
        <div className="relative">
          <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${getColorClass()}`}
              style={{ width: `${percentage}%` }}
            />
          </div>

          {/* Warning/Alert Messages */}
          {isOverLimit && (
            <div className="mt-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900">Limit exceeded</p>
                <p className="text-xs text-red-700 mt-0.5">
                  You've reached your plan limit. Upgrade to continue.
                </p>
                {showAddOn && onAddOn && (
                  <p className="text-xs text-red-700 mt-1">
                    Or{' '}
                    <button
                      onClick={onAddOn}
                      disabled={addOnLoading}
                      className="font-semibold underline hover:no-underline"
                    >
                      {addOnLoading ? 'Processing...' : 'add 1 extra trainer for ₹1,000/month'}
                    </button>
                  </p>
                )}
              </div>
              {showUpgrade && onUpgrade && (
                <button
                  onClick={onUpgrade}
                  className="text-xs font-medium text-red-700 hover:text-red-800 underline flex-shrink-0"
                >
                  Upgrade
                </button>
              )}
            </div>
          )}

          {isNearLimit && !isOverLimit && (
            <div className="mt-2 flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
              <svg
                className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-900">Approaching limit</p>
                <p className="text-xs text-orange-700 mt-0.5">
                  You're using {Math.round(percentage)}% of your {label.toLowerCase()}.
                </p>
                {showAddOn && onAddOn && (
                  <p className="text-xs text-orange-700 mt-1">
                    You can{' '}
                    <button
                      onClick={onAddOn}
                      disabled={addOnLoading}
                      className="font-semibold underline hover:no-underline"
                    >
                      {addOnLoading ? 'Processing...' : 'add 1 extra trainer for ₹1,000/month'}
                    </button>
                  </p>
                )}
              </div>
              {showUpgrade && onUpgrade && (
                <button
                  onClick={onUpgrade}
                  className="text-xs font-medium text-orange-700 hover:text-orange-800 underline flex-shrink-0"
                >
                  Upgrade
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageMeter;
